import type {
  BillingProfileResponse,
  BillingSetupRequest,
  BillingSetupResponse,
  BillingCompleteSetupRequest,
  TopupRequest,
  TopupResponse,
  LedgerResponse,
  LedgerListParams,
  BlockedAttemptListResponse,
  BlockedAttemptListParams,
  AutoRechargeUpdateRequest,
  AllocationTransferRequest,
  AllocationTransferResponse,
  AllocationListResponse,
  AllocationResponse,
} from '../types';
import type { RequestFn } from '../client';

/** Manage per-shop prepaid balance allocations transferred from the host merchant treasury. */
class BillingAllocations {
  constructor(private readonly request: RequestFn) {}

  /**
   * Transfer funds from the host merchant treasury into a shop's allocation.
   *
   * @param merchantId - The host merchant account ID.
   * @param params - Transfer details (amount, target profile/shop ID).
   * @returns The allocation transfer result.
   */
  async transferIn(
    merchantId: string,
    params: AllocationTransferRequest,
  ): Promise<AllocationTransferResponse> {
    return this.request(
      'POST',
      `/billing/${encodeURIComponent(merchantId)}/allocations/transfer-in`,
      { body: params },
    );
  }

  /**
   * Transfer funds from a shop's allocation back to the host merchant treasury.
   *
   * @param merchantId - The host merchant account ID.
   * @param params - Transfer details (amount, source profile/shop ID).
   * @returns The allocation transfer result.
   */
  async transferOut(
    merchantId: string,
    params: AllocationTransferRequest,
  ): Promise<AllocationTransferResponse> {
    return this.request(
      'POST',
      `/billing/${encodeURIComponent(merchantId)}/allocations/transfer-out`,
      {
        body: params,
      },
    );
  }

  /**
   * List all shop balance allocations for a merchant.
   *
   * @param merchantId - The merchant account ID.
   * @returns List of shop allocations.
   */
  async list(merchantId: string): Promise<AllocationListResponse> {
    return this.request('GET', `/billing/${encodeURIComponent(merchantId)}/allocations`);
  }

  /**
   * Get the balance allocation for a specific shop.
   *
   * @param merchantId - The merchant account ID.
   * @param profileId - The shop (business profile) ID.
   * @returns The shop's balance allocation.
   */
  async get(merchantId: string, profileId: string): Promise<AllocationResponse> {
    return this.request(
      'GET',
      `/billing/${encodeURIComponent(merchantId)}/allocations/${encodeURIComponent(profileId)}`,
    );
  }
}

/**
 * Manage prepaid billing balances — top-ups, card setup, auto-recharge, and the balance ledger.
 *
 * Delopay deducts a platform fee from the merchant's prepaid balance on every successful payment.
 * Use these endpoints to fund and monitor that balance.
 */
export class Billing {
  /** Per-shop balance allocation management for host merchants. */
  readonly allocations: BillingAllocations;

  constructor(private readonly request: RequestFn) {
    this.allocations = new BillingAllocations(request);
  }

  /**
   * Retrieve a merchant's billing profile (balance, status, auto-recharge config).
   *
   * @param merchantId - The merchant account ID.
   * @returns The billing profile.
   *
   * @example
   * ```typescript
   * const profile = await delopay.billing.getProfile('merch_123');
   * console.log(profile.balance, profile.status);
   * ```
   */
  async getProfile(merchantId: string): Promise<BillingProfileResponse> {
    return this.request('GET', `/billing/${encodeURIComponent(merchantId)}`);
  }

  /**
   * Start a Stripe SetupIntent flow to collect a payment card for auto-recharge.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Optional setup parameters.
   * @returns The Stripe client secret needed to render the card element.
   */
  async setup(merchantId: string, params?: BillingSetupRequest): Promise<BillingSetupResponse> {
    return this.request('POST', `/billing/${encodeURIComponent(merchantId)}/setup`, {
      body: params,
    });
  }

  /**
   * Confirm card setup after the Stripe SetupIntent completes on the frontend.
   *
   * @param merchantId - The merchant account ID.
   * @param params - The Stripe SetupIntent ID to confirm.
   * @returns The updated billing profile.
   */
  async completeSetup(
    merchantId: string,
    params: BillingCompleteSetupRequest,
  ): Promise<BillingProfileResponse> {
    return this.request('POST', `/billing/${encodeURIComponent(merchantId)}/setup/complete`, {
      body: params,
    });
  }

  /**
   * Manually top up a merchant's prepaid balance by charging their saved card.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Top-up amount and currency.
   * @returns The top-up result.
   */
  async topup(merchantId: string, params: TopupRequest): Promise<TopupResponse> {
    return this.request('POST', `/billing/${encodeURIComponent(merchantId)}/topup`, {
      body: params,
    });
  }

  /**
   * List the balance ledger (credits and debits) for a merchant.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Optional pagination parameters.
   * @returns The ledger entries.
   */
  async listLedger(merchantId: string, params?: LedgerListParams): Promise<LedgerResponse> {
    return this.request('GET', `/billing/${encodeURIComponent(merchantId)}/ledger`, {
      query: params as Record<string, string | number | undefined>,
    });
  }

  /**
   * List payment attempts that were blocked by the billing suspension gate
   * (account suspended, setup incomplete, or shop allocation suspended).
   *
   * These attempts never created a payment, so they do not appear in the
   * payments list — this is the only way to retrieve them.
   *
   * @param merchantId - The merchant account ID.
   * @param params - Optional filters (profile, reason, date range) and pagination.
   * @returns The blocked-attempt entries with a total count.
   */
  async listBlockedAttempts(
    merchantId: string,
    params?: BlockedAttemptListParams,
  ): Promise<BlockedAttemptListResponse> {
    return this.request('GET', `/billing/${encodeURIComponent(merchantId)}/blocked-attempts`, {
      query: params as Record<string, string | number | undefined>,
    });
  }

  /**
   * Update auto-recharge configuration (threshold, top-up amount, enabled flag).
   *
   * @param merchantId - The merchant account ID.
   * @param params - Auto-recharge settings to update.
   * @returns The updated billing profile.
   */
  async updateAutoRecharge(
    merchantId: string,
    params: AutoRechargeUpdateRequest,
  ): Promise<BillingProfileResponse> {
    return this.request('PATCH', `/billing/${encodeURIComponent(merchantId)}/auto-recharge`, {
      body: params,
    });
  }
}
